import { type MouseEvent, memo, type ReactNode, useEffect } from 'react';
import { type BackgroundProps } from 'react-imgix';

import { type Board } from '@air/api/types';
import { ImgixImage, type ImgixImageProps } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { RiveThumbnail } from '~/components/RiveThumbnail';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';

export type BoardThumbnailProps = {
  className?: string;
  board: Pick<Board, 'thumbnails' | 'title'>;
  /**
   * Accepts a CSS string (not just a number) so surfaces like the public collection page can
   * pass a var() with the native 4px fallback; numbers keep the px-suffixing inline-style behavior.
   */
  borderRadius?: number | string;
  children?: ReactNode;
  imgixHeight?: number;
  imgixWidth?: number;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFallbackChange?: (isFallback: boolean) => void;
  /** This is the img "sizes" attribute that should be used to determine the board thumbnail sizes */
  sizes: ImgixImageProps['sizes'];
  width: string | number;
  height: string | number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down' | 'none' | 'initial' | 'inherit';
  imgixParams?: BackgroundProps['imgixParams'];
};

/**
 * This is used to display board's thumbnail (only image)
 */
const BoardThumbnail = memo(
  ({
    board: { thumbnails, title },
    children,
    sizes,
    width,
    height,
    borderRadius = 4,
    onClick,
    imgixHeight = 400,
    imgixParams,
    imgixWidth = 400,
    className,
    objectFit = 'contain',
    onFallbackChange,
  }: BoardThumbnailProps) => {
    const thumbnail = thumbnails?.length ? thumbnails[0] : null;
    const isBase64Image = thumbnail?.startsWith('data:image');
    const isRiveFile = thumbnail?.toLowerCase().endsWith('.riv');
    const fallbackUrl = thumbnail ? getCloudFrontUrlFromImgixUrl(thumbnail) : '';
    const shouldShowFallback = !isBase64Image && !isRiveFile && !thumbnail;

    useEffect(() => {
      onFallbackChange?.(shouldShowFallback);
    }, [shouldShowFallback, onFallbackChange]);

    const renderThumbnail = (alt: string) => {
      if (isBase64Image && thumbnail) {
        return (
          // biome-ignore lint/performance/noImgElement: board thumbnails accept data URIs that next/image cannot load
          <img src={thumbnail} alt={alt} style={{ objectFit }} />
        );
      }

      if (isRiveFile && thumbnail) {
        return (
          <RiveThumbnail src={thumbnail} width={Number(width)} height={Number(height)} borderRadius={borderRadius} />
        );
      }

      if (thumbnail) {
        return (
          <ImgixImage
            alt={alt}
            fallbackUrl={fallbackUrl}
            height={imgixHeight}
            imgixParams={{
              w: 400,
              h: 400,
              fit: 'contain',
              cs: 'origin',
              ...imgixParams,
            }}
            sizes={sizes}
            src={thumbnail}
            style={{ objectFit }}
            width={imgixWidth}
          />
        );
      }

      return <div className="size-full ads:bg-subtle bg-gray-4" />;
    };

    const frameStyle = {
      cursor: onClick ? 'pointer' : 'default',
      width,
      height,
      minWidth: width,
      borderRadius,
    };
    const imageAlt = onClick ? '' : title;
    const thumbnailContent = (
      <>
        {renderThumbnail(imageAlt)}
        {children}
      </>
    );

    if (onClick) {
      return (
        <button
          type="button"
          aria-label={title}
          data-testid="BOARD_CARD_THUMBNAIL"
          data-title={title}
          className={cn(
            'relative overflow-hidden border-0 bg-transparent p-0 text-inherit [&>img]:relative [&>img]:h-full [&>img]:w-full',
            'cursor-pointer',
            className,
          )}
          style={frameStyle}
          onClick={onClick}
        >
          {thumbnailContent}
        </button>
      );
    }

    return (
      <div
        data-testid="BOARD_CARD_THUMBNAIL"
        data-title={title}
        className={cn(
          'relative overflow-hidden [&>img]:relative [&>img]:h-full [&>img]:w-full',
          'cursor-default',
          className,
        )}
        style={frameStyle}
      >
        {thumbnailContent}
      </div>
    );
  },
);

BoardThumbnail.displayName = 'BoardThumbnail';

export default BoardThumbnail;
